import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronUp, History, Package, Wrench, Box } from "lucide-react";

export function ReceptionsHistory({ poId, refreshKey }) {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-orders/${poId}/receptions`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [poId, refreshKey]);

  const totalReceptions = items.length;

  return (
    <Card className="border-border" data-testid="po-receptions-history">
      <CardHeader className="border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" /> Historial de recepciones
            <span className="text-xs text-muted-foreground font-normal">
              ({loading ? "…" : `${totalReceptions} ${totalReceptions === 1 ? "recepción" : "recepciones"}`})
            </span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)} data-testid="po-receptions-toggle">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-4 space-y-4">
          {loading ? (
            <Skeleton className="h-16" />
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">
              Aún no se ha registrado ninguna recepción para esta OC.
            </div>
          ) : (
            items.map((r) => (
              <div key={r.id} className="border border-border rounded-md p-3 bg-slate-50/50" data-testid={`po-reception-${r.id}`}>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>
                    <b className="text-slate-800">{r.received_by_name || "—"}</b> ·{" "}
                    {new Date(r.received_at).toLocaleString("es-MX")}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {r.stock_movement_ids?.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Box className="h-3 w-3" />
                        {r.stock_movement_ids.length} movimiento{r.stock_movement_ids.length === 1 ? "" : "s"} kardex
                      </span>
                    )}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {(r.lines || []).map((e, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      {e.line_type === "inventariable"
                        ? <Package className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                        : <Wrench className="h-3.5 w-3.5 text-purple-600 shrink-0" />}
                      <span className="flex-1 truncate">{e.description}</span>
                      <span className="mono text-xs">
                        <b>{Number(e.qty_received).toFixed(2)}</b> ×{" "}
                        <span className="text-muted-foreground">${Number(e.unit_cost_snapshot || 0).toFixed(2)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                {r.notes && (
                  <p className="text-xs text-muted-foreground italic mt-2 pt-2 border-t border-border/50">
                    “{r.notes}”
                  </p>
                )}
              </div>
            ))
          )}
        </CardContent>
      )}
    </Card>
  );
}
